import React, { useEffect, useState } from "react";
import { Euro, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/UI";

// Dashboard.jsx'ten taşındı (MetaERP TCMB kur dönüştürücü widget'ı).
export function FxConverterCard() {
  // iter239.1 — Multi-currency FX converter (TRY/EUR/USD) backed by the
  // canonical /api/fx/convert TCMB endpoint. Replaces the earlier
  // TRY↔EUR-only widget without changing widget id, size or layout.
  const [fxAmount, setFxAmount] = useState(100);
  const [fxBase, setFxBase] = useState("EUR");
  const [fxQuote, setFxQuote] = useState("TRY");
  const [fxResult, setFxResult] = useState(null);   // { converted, rate, rate_date, source }
  const [fxLoading, setFxLoading] = useState(false);
  const [fxError, setFxError] = useState(null);

  // iter239.1 — Debounced conversion so typing an amount does NOT hit the
  // API on every keystroke. Same-currency short-circuits without a call.
  // No page reload; widget size + Dashboard state are preserved.
  useEffect(() => {
    const amt = parseFloat(fxAmount);
    if (!isFinite(amt)) return;
    if (fxBase === fxQuote) {
      setFxResult({ converted: amt, rate: 1, rate_date: fxResult?.rate_date || "",
                     source: "TCMB", rate_type: "ForexSelling" });
      setFxError(null);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setFxLoading(true); setFxError(null);
      try {
        const r = await api.get("/fx/convert",
          { amount: amt, base: fxBase, quote: fxQuote },
          { signal: ctrl.signal });
        setFxResult(r);
      } catch (e) {
        if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") {
          setFxError(e.response?.data?.detail || "Kur alınamadı");
        }
      } finally { setFxLoading(false); }
    }, 350);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [fxAmount, fxBase, fxQuote]);

  return (
      <Card className="p-3 mb-4 bg-gradient-to-r from-indigo-50/70 to-blue-50/70 dark:from-indigo-900/20 dark:to-blue-900/20 border-indigo-200/60 dark:border-indigo-500/30" data-testid="fx-converter">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 pr-2 border-r border-indigo-200 dark:border-indigo-500/30">
            <Euro size={14} className="text-indigo-600 dark:text-indigo-400"/>
            <span className="text-xs font-semibold text-indigo-900 dark:text-indigo-300">Kur Dönüştürücü</span>
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[10px] uppercase text-slate-500 dark:text-slate-400">Tutar</label>
            <input
              type="number"
              step="0.01"
              value={fxAmount}
              onChange={e => setFxAmount(e.target.value)}
              className="w-28 px-2 py-1 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/70 dark:text-slate-100 rounded font-mono"
              data-testid="fx-input-amount"
            />
          </div>
          <select
            value={fxBase}
            onChange={e => setFxBase(e.target.value)}
            className="px-2 py-1 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/70 dark:text-slate-100 rounded"
            data-testid="fx-select-base"
          >
            <option value="TRY">TRY</option>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
          </select>
          <ChevronRight size={12} className="text-slate-400"/>
          <select
            value={fxQuote}
            onChange={e => setFxQuote(e.target.value)}
            className="px-2 py-1 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/70 dark:text-slate-100 rounded"
            data-testid="fx-select-quote"
          >
            <option value="TRY">TRY</option>
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
          </select>
          <div className="flex items-center gap-1.5" data-testid="fx-result">
            <span className="text-xs uppercase text-slate-500 dark:text-slate-400">Sonuç</span>
            <span className="font-mono text-sm font-semibold text-indigo-900 dark:text-indigo-200 min-w-[7rem] text-right" data-testid="fx-result-value">
              {fxLoading ? "…" : (fxResult ? Number(fxResult.converted).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : "—")}
            </span>
            <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono">{fxQuote}</span>
          </div>
          {fxResult && !fxError && (
            <span className="text-[10px] text-slate-500 dark:text-slate-400 font-mono" data-testid="fx-rate-line">
              1 {fxBase} = {Number(fxResult.rate || 0).toFixed(4)} {fxQuote}
            </span>
          )}
          <span className="ml-auto text-[10px] text-slate-400 dark:text-slate-500" data-testid="fx-source-info">
            {fxError ? <span className="text-rose-600">{String(fxError)}</span> : (
              <>Kaynak: <b className="text-slate-600 dark:text-slate-300">TCMB</b>{fxResult?.rate_type ? ` (${fxResult.rate_type})` : ""} · Tarih: <span className="font-mono">{fxResult?.rate_date || "—"}</span></>
            )}
          </span>
        </div>
      </Card>
  );
}
